'use client';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@clerk/nextjs';
import {
  getDaysRemainingClient,
  hasFeatureFlagClient,
  hasPaidSubscriptionClient,
  isCustomViewClient,
  isManagedOrgClient,
  isAdminClient,
  isSubscriptionExpiredClient,
  isSubscriptionViewClient,
  isSuperAdminViewClient,
} from '@/lib/permissions/client';
import { showEvaluateNavItemClient } from '@/lib/evaluate/evaluate-page-access-client';
import { EVALUATE_PAGE_SUPER_ADMIN_ONLY_TEMP } from '@/lib/evaluate/evaluate-page-access';
import {
  isCampaignsAvailableClient,
  isTemplatesAvailableClient,
} from '@/lib/permissions/automation-access';
import { hasWhatsAppSequencesFlagClient } from '@/lib/permissions/whatsapp-sequences-flag';
import type { AppNavItem } from '@/lib/navigation/app-nav-types';
import { placeSuperAdminNavItemsLast } from '@/lib/navigation/place-super-admin-nav-items';
import { useAppShell } from '@/components/layout/app-shell-context';

export function useAppNavItems(): {
  navItems: AppNavItem[];
  shouldShowItems: boolean;
  isCustomView: boolean;
  isSubscriptionView: boolean;
  isSuperAdmin: boolean;
  hasPaid: boolean;
  usageTabColor: 'red' | 'yellow' | null;
} {
  const { t } = useTranslation();
  const { orgRole, sessionClaims } = useAuth();
  const { variant: shellVariant } = useAppShell();

  const isSuperAdmin = isSuperAdminViewClient(orgRole, sessionClaims);
  const isCustomView = isCustomViewClient(sessionClaims);
  const isManagedOrg = isManagedOrgClient(sessionClaims);
  const isSubscriptionView = isSubscriptionViewClient(orgRole, sessionClaims);
  const hasArboxAutomationsFeature = hasFeatureFlagClient(sessionClaims, 'arbox-automations');
  const hasTriggersFeature = hasFeatureFlagClient(sessionClaims, 'triggers');
  const hasCustomersFeature = hasFeatureFlagClient(sessionClaims, 'customers');
  const hasWhatsAppSequencesFeature = hasWhatsAppSequencesFlagClient(sessionClaims);
  const showEvaluateNav = showEvaluateNavItemClient(orgRole, sessionClaims);
  const showEvaluateInMainNav = showEvaluateNav && !EVALUATE_PAGE_SUPER_ADMIN_ONLY_TEMP;
  const showCampaignsNav = isCampaignsAvailableClient(sessionClaims);
  const showTemplatesNav = isTemplatesAvailableClient(sessionClaims);
  const hasPaid = hasPaidSubscriptionClient(sessionClaims);

  const daysRemaining = getDaysRemainingClient(sessionClaims);
  const isExpired = isSubscriptionExpiredClient(sessionClaims);
  const usageTabColor = useMemo((): 'red' | 'yellow' | null => {
    if (daysRemaining === 0 || isExpired) return 'red';
    if (daysRemaining > 0) return 'yellow';
    return null;
  }, [daysRemaining, isExpired]);

  const navItems = useMemo(() => {
    const chatsItem: AppNavItem = { href: '/chats', label: t('navigation.chats'), icon: 'messages' };
    const customersItem: AppNavItem = {
      href: '/customers',
      label: t('navigation.customers'),
      icon: 'customers',
    };
    const simulationItem: AppNavItem = {
      href: '/test-agent',
      label: t('navigation.testChat'),
      icon: 'simulation',
    };
    const evaluateItem: AppNavItem = {
      href: '/evaluate',
      label: t('navigation.evaluate'),
      icon: 'evaluate',
    };
    const settingsLinkItem: AppNavItem = { href: '/agent', label: t('navigation.settings'), icon: 'settings' };
    const connectionItem: AppNavItem = { href: '/channels', label: t('navigation.channel'), icon: 'link' };
    const usageItem: AppNavItem = { href: '/usage', label: t('navigation.usage'), icon: 'usage' };
    const billingItem: AppNavItem = { href: '/billing', label: t('navigation.billing'), icon: 'billing' };
    const ticketsItem: AppNavItem = {
      href: '/tickets',
      label: t('tickets.title'),
      icon: 'tickets',
    };

    const customersDropdownItem = {
      href: '/customers',
      label: t('navigation.customers'),
    };

    /** Marketing group (formerly Auto Messages / אוטומציות): audience + outreach tools. */
    const marketingDropdown = (dropdownItems: Array<{ href: string; label: string }>): AppNavItem => ({
      href: '#',
      label: t('navigation.marketing'),
      icon: 'outreach',
      isDropdown: true,
      mobileDesktopOnly: true,
      dropdownItems,
    });

    /**
     * Marketing parent whenever a template-dependent capability is on. Templates is always a
     * child; other items follow their own flags.
     */
    const marketingNavItem: AppNavItem | null = showTemplatesNav
      ? marketingDropdown([
          ...(hasCustomersFeature ? [customersDropdownItem] : []),
          ...(showCampaignsNav ? [{ href: '/campaigns', label: t('navigation.campaigns') }] : []),
          ...(hasWhatsAppSequencesFeature
            ? [{ href: '/sequences', label: t('navigation.sequences') }]
            : []),
          ...(hasArboxAutomationsFeature
            ? [{ href: '/arbox-automations', label: t('navigation.arboxAutomations') }]
            : []),
          ...(hasTriggersFeature ? [{ href: '/triggers', label: t('navigation.triggers') }] : []),
          { href: '/templates', label: t('navigation.templates') },
        ])
      : null;

    const isAdmin = isAdminClient(orgRole);
    const shouldShowBillingOnNavbar = !hasPaid && isAdmin;

    const moreNavItem: AppNavItem = {
      href: '#',
      label: t('navigation.more'),
      icon: 'more',
      isDropdown: true,
      dropdownItems: [
        { href: '/academy', label: t('navigation.academy') },
        { href: '/integrations', label: t('navigation.integrations') },
        { href: '/votes?status=pending,approved', label: t('navigation.votes') },
      ],
    };

    const adminOnlyItems = (items: AppNavItem[]): AppNavItem[] => {
      if (isAdmin) return items;
      return items.flatMap((item) => {
        if (
          item.href === '/agent' ||
          item.href === '/integrations' ||
          item.href === '/channels' ||
          item.href === '/usage' ||
          item.href === '/billing' ||
          item.href === '/evaluate'
        ) {
          return [];
        }
        if (!item.dropdownItems) return [item];
        const dropdownItems = item.dropdownItems.filter((child) => child.href !== '/integrations');
        if (dropdownItems.length === 0) return [];
        return [{ ...item, dropdownItems }];
      });
    };

    /** Sidebar shell: tickets last (bottom after reverse). Custom orgs + super-admin sidebar override. */
    const appendSidebarTickets = (items: AppNavItem[]): AppNavItem[] => {
      if (shellVariant !== 'custom') return items;
      if (!isManagedOrg && !isSuperAdmin) return items;
      return [ticketsItem, ...items];
    };

    /**
     * Simulation sits before agent settings in nav order (sidebar display is reversed).
     */
    const insertSimulationBeforeSettings = (items: AppNavItem[]): AppNavItem[] => {
      const settingsIndex = items.findIndex((item) => item.href === '/agent');
      if (settingsIndex === -1) {
        return [...items, simulationItem];
      }
      return [
        ...items.slice(0, settingsIndex),
        simulationItem,
        ...items.slice(settingsIndex),
      ];
    };

    if (isSuperAdmin) {
      const items = adminOnlyItems([
        moreNavItem,
        ...(shouldShowBillingOnNavbar ? [billingItem] : []),
        usageItem,
        connectionItem,
        ...(showEvaluateInMainNav ? [evaluateItem] : []),
        ...(marketingNavItem ? [marketingNavItem] : []),
        settingsLinkItem,
        // Customers lives under Marketing when that group is shown.
        ...(!marketingNavItem && hasCustomersFeature ? [customersItem] : []),
        chatsItem,
      ]);
      return appendSidebarTickets(
        placeSuperAdminNavItemsLast(insertSimulationBeforeSettings(items)),
      );
    }

    if (isSubscriptionView) {
      return placeSuperAdminNavItemsLast(
        insertSimulationBeforeSettings(
          adminOnlyItems([
            moreNavItem,
            ...(shouldShowBillingOnNavbar ? [billingItem] : []),
            usageItem,
            connectionItem,
            ...(showEvaluateInMainNav ? [evaluateItem] : []),
            ...(marketingNavItem ? [marketingNavItem] : []),
            settingsLinkItem,
            ...(!marketingNavItem && hasCustomersFeature ? [customersItem] : []),
            chatsItem,
          ]),
        ),
      );
    }

    if (isCustomView) {
      return appendSidebarTickets(
        placeSuperAdminNavItemsLast(
          insertSimulationBeforeSettings(
            adminOnlyItems([
              moreNavItem,
              ...(shouldShowBillingOnNavbar ? [billingItem] : []),
              usageItem,
              connectionItem,
              ...(showEvaluateInMainNav ? [evaluateItem] : []),
              ...(marketingNavItem ? [marketingNavItem] : []),
              settingsLinkItem,
              ...(!marketingNavItem && hasCustomersFeature ? [customersItem] : []),
              chatsItem,
            ]),
          ),
        ),
      );
    }

    return placeSuperAdminNavItemsLast(
      insertSimulationBeforeSettings(
        adminOnlyItems([
          moreNavItem,
          ...(shouldShowBillingOnNavbar ? [billingItem] : []),
          usageItem,
          connectionItem,
          ...(showEvaluateInMainNav ? [evaluateItem] : []),
          ...(marketingNavItem ? [marketingNavItem] : []),
          ...(!marketingNavItem && hasCustomersFeature ? [customersItem] : []),
          chatsItem,
        ]),
      ),
    );
  }, [
    t,
    orgRole,
    isSuperAdmin,
    isSubscriptionView,
    isCustomView,
    isManagedOrg,
    shellVariant,
    hasArboxAutomationsFeature,
    hasTriggersFeature,
    hasCustomersFeature,
    hasWhatsAppSequencesFeature,
    showEvaluateInMainNav,
    showCampaignsNav,
    showTemplatesNav,
    hasPaid,
  ]);

  return {
    navItems,
    shouldShowItems: true,
    isCustomView,
    isSubscriptionView,
    isSuperAdmin,
    hasPaid,
    usageTabColor,
  };
}
